import { createContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { adminAxios, adminLogout } from '../../services/api.admin';
import { AdminAuthContextType, AdminUserInfo } from '../../types/auth';

export const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export const AdminAuthProvider = ({ children }: { children: ReactNode }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const {
        data: user,
        isLoading: loading,
        error
    } = useQuery<AdminUserInfo | null>({
        queryKey: ['adminUser'],
        queryFn: async () => {
            try {
                const res = await adminAxios.get<AdminUserInfo>('/admin/auth/me');
                return res.data;
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 401) {
                    return null;
                }
                throw err;
            }
        },
        enabled: typeof window !== 'undefined' &&
            (document.cookie.includes('adminToken') || window.location.pathname.startsWith('/admin')),
        staleTime: 15 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
    });

    const isAuthenticated = !!user;

    const login = async (email: string, password: string) => {
        await adminAxios.post('/admin/auth/login', { email, password });
        await queryClient.invalidateQueries({ queryKey: ['adminUser'] });
        // replace: Back from admin should not return to the login form
        navigate('/admin', { replace: true });
    };

    const logout = async () => {
        try {
            await adminLogout();
        } catch (err) {
            console.warn('Logout failed');
        }
        queryClient.setQueryData(['adminUser'], null);
        // replace: do not keep authenticated admin screens on the Back stack
        navigate('/admin/login', { replace: true });
    };

    const isAdmin = () => {
        if (!user?.roles) return false;
        return Array.isArray(user.roles)
            ? user.roles.includes('Admin')
            : user.roles === 'Admin';
    };

    if (error) {
        console.error('Admin auth query error:', error);
    }

    return (
        <AdminAuthContext.Provider
            value={{
                user: user ?? null,
                isAuthenticated,
                loading,
                login,
                logout,
                isAdmin,
            }}
        >
            {children}
        </AdminAuthContext.Provider>
    );
};